import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Project } from "@/api/studio/projects/types/project.type";
import { useGetProjects } from "@/api/studio/projects/queries";
import { ProjectsLayout } from "@/components/layouts";
import { Bars3Icon, LockClosedIcon, PlusIcon, Squares2X2Icon, UsersIcon } from "@heroicons/react/24/solid";
import { asRelativeDate } from "@/utils/format";
import { useModalStore } from "@/stores/use-modal-store";
import { CreateProjectModal } from "@/features/projects/modals/create-project-modal";
import { ProjectsTableSkeleton } from "@/features/projects/components/projects-table-skeleton";
import { ProjectDropdown } from "@/features/projects/components/project-dropdown";
import { Pagination } from "@/components/ui/pagination/pagination";
import { Search } from "@/components/ui/search";
import { Button } from "@/components/ui/button";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { Card, CardBadge, CardBody, CardSkeleton } from "@/components/ui/card";
import { Table, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { EmptyState } from "@/features/projects/components/empty-state";
import { projectThumbnails } from "@/assets/project-thumbnails";
import { GetProjectsReq } from "@/api/studio/projects/api";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { SelectSort } from "@/components/ui/select";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

/*
 * ProjectsRoute component
 * Page to view all projects
 */
export const ProjectsRoute = () => {
  /* Stores */
  const [, navigate] = useLocation();
  const { open } = useModalStore((state) => state.modalActions);
  const theme = useGlobalStore((state) => state.theme);

  /* Hooks */
  const { segmentPageEvent } = useSegment();

  /* State */
  const [query, setQuery] = useState("");
  const [view, setView] = useState<GetProjectsReq["view"]>("all");
  const [orderBy, setOrderBy] = useState<GetProjectsReq["orderBy"]>("updatedAt");
  const [sortBy, setSortBy] = useState<GetProjectsReq["sortBy"]>("desc");
  const [pageIndex, setPageIndex] = useState(1);
  const [layoutView, setLayoutView] = useState("List");

  /* Queries */
  const { data: projects, isLoading: isProjectsLoading } = useGetProjects({ query, sortBy, orderBy, pageIndex, itemsPerPage: 10, view });

  /* Handlers */
  const handleOpen = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleCreateProject = () => {
    open(<CreateProjectModal />);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Reset to first page on search
    setPageIndex(1);
    setQuery(e.target.value);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the projects page
    if (theme)
      segmentPageEvent("Projects", {
        title: "Projects | Dragonfly AI Studio",
        theme: theme,
      });
  }, []);

  /* Render */
  return (
    <ProjectsLayout>
      {/* Primary */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Projects</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" onClick={handleCreateProject}>
            <PlusIcon className="size-5" /> New project
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary */}
      <Toolbar>
        <ToolbarItem>
          <Search onChange={handleSearch} />
          <CustomDropdown
            triggerBtn={
              <Button>
                {view === "public" && "Shared"}
                {view && view !== "public" && view.charAt(0).toUpperCase() + view.slice(1)}
                <ChevronDownIcon className="size-4" />
              </Button>
            }
            triggerBtnClassname="p-0"
          >
            <CustomDropdownItem onClick={() => setView("all")}>All</CustomDropdownItem>
            <CustomDropdownItem onClick={() => setView("private")}>Private</CustomDropdownItem>
            <CustomDropdownItem onClick={() => setView("public")}>Shared</CustomDropdownItem>
            <CustomDropdownItem onClick={() => setView("archived")}>Archived</CustomDropdownItem>
          </CustomDropdown>
        </ToolbarItem>
        <ToolbarItem>
          <CustomDropdown
            align="end"
            triggerBtnClassname="p-0"
            triggerBtn={
              <Button>
                {layoutView === "Cards" ? <Squares2X2Icon className="size-3.5" /> : <Bars3Icon className="size-3.5" />}
                {layoutView}
              </Button>
            }
          >
            <CustomDropdownItem onClick={() => setLayoutView("Cards")}>
              <Squares2X2Icon className="size-4" />
              Cards
            </CustomDropdownItem>
            <CustomDropdownItem onClick={() => setLayoutView("List")}>
              <Bars3Icon className="size-4" />
              List
            </CustomDropdownItem>
          </CustomDropdown>
        </ToolbarItem>
      </Toolbar>

      {/* Tertiary */}
      <Toolbar>
        <ToolbarItem>
          <h2>{projects?.maxRecordCount} projects</h2>
        </ToolbarItem>
        <ToolbarItem>
          <SelectSort setOrderBy={setOrderBy} setSortBy={setSortBy} />
        </ToolbarItem>
      </Toolbar>

      {/* Projects */}
      <div className="flex flex-col gap-7 pb-20">
        {layoutView === "Cards" ? (
          <div className="flex gap-4 flex-wrap w-full">
            {/* Skeleton loader */}
            {isProjectsLoading && Array.from({ length: 5 }).map((_, index) => <CardSkeleton key={index} />)}

            {/* Empty fallback */}
            {projects?.projects.length === 0 && <EmptyState title="This account has no projects" isProjectEmpty={true} />}

            {/* Card view */}
            {projects?.projects.map((project) => (
              <Card key={project.id} title={project.name} imageUrl={projectThumbnails[project.thumbnailName]} onClick={() => handleOpen(project)} className="group">
                <CardBody className="relative" title={project.name}>
                  <>
                    <p className="text-base-content/60">
                      {project.studyCount} {project.studyCount === 1 ? "study" : "studies"}
                    </p>
                    <div className="absolute top-5 right-4">
                      <ProjectDropdown project={project} />
                    </div>
                  </>
                </CardBody>
                <CardBadge>
                  {project.visibility === "private" && (
                    <>
                      <LockClosedIcon className="size-3.5" /> Private
                    </>
                  )}
                  {project.visibility === "public" && (
                    <>
                      <UsersIcon className="size-3.5" /> Shared
                    </>
                  )}
                </CardBadge>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Skeleton Loader */}
            {isProjectsLoading && <ProjectsTableSkeleton />}

            {/* Empty fallback */}
            {projects?.projects.length === 0 && <EmptyState title="This account has no projects" isProjectEmpty={true} />}

            {/* List view */}
            {!isProjectsLoading && !!projects?.projects.length && (
              <Table>
                <thead>
                  <TableRow>
                    <TableHeader>Project</TableHeader>
                    <TableHeader>Access</TableHeader>
                    <TableHeader>Author</TableHeader>
                    <TableHeader>Last edited</TableHeader>
                    <TableHeader></TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {projects?.projects.map((project) => (
                    <TableRow key={project.id} onClick={() => handleOpen(project)}>
                      <TableCell>
                        <div
                          className="size-12 rounded-lg shrink-0"
                          style={{ backgroundImage: `url(${projectThumbnails[project.thumbnailName]})`, backgroundSize: "cover", backgroundPosition: "center" }}
                        ></div>
                        <div className="flex flex-col gap-1 truncate">
                          <p className="font-semibold truncate">{project.name}</p>
                          <p className="text-sm text-base-content/50">
                            {project.studyCount} {project.studyCount === 1 ? "study" : "studies"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {project.visibility === "private" ? (
                          <>
                            <LockClosedIcon className="size-3.5 shrink-0" />
                            <p className="text-sm truncate">Private</p>
                          </>
                        ) : (
                          <>
                            <UsersIcon className="size-3.5 shrink-0" />
                            <p className="text-sm truncate">Shared</p>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm truncate">{project.username}</p>
                      </TableCell>
                      <TableCell>
                        {" "}
                        <p className="text-sm truncate">{asRelativeDate(project.updatedAt)}</p>
                      </TableCell>
                      <TableCell className="justify-end" truncate={false}>
                        <ProjectDropdown project={project} />
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {/* Pagination */}
        <Pagination currentPage={pageIndex} totalPages={projects?.maxPageIndex ?? 1} onPageChange={setPageIndex} />
      </div>
    </ProjectsLayout>
  );
};
